import { useCallback, useEffect, useRef } from 'react';
import { Box, List, ListItem, Typography } from '@mui/material';
import type { INotification } from '../models/Notification';
import NotificationItem from './NotificationItem';

export interface NotificationsListProps {
  notifications: INotification[];
  setIsReadNotification: (notification: INotification, isRead: boolean) => void;
  onScrolledToBottomChange?: (isScrolledToBottom: boolean) => void;
  listEmptyWhileFetching?: boolean;
}

const NotificationsList: React.FC<NotificationsListProps> = ({
  notifications,
  setIsReadNotification,
  onScrolledToBottomChange,
  listEmptyWhileFetching = false,
}) => {
  const listRef = useRef<HTMLUListElement | null>(null);
  const endOfListRef = useRef<HTMLLIElement | null>(null);
  const hasNotifications = notifications.length > 0;

  const updateScrollPosition = useCallback(
    (element: HTMLUListElement) => {
      const isScrolledToBottom =
        element.scrollTop + element.clientHeight >= element.scrollHeight - 2;

      onScrolledToBottomChange?.(isScrolledToBottom);
    },
    [onScrolledToBottomChange]
  );

  const handleListScroll = (event: React.UIEvent<HTMLUListElement>) => {
    updateScrollPosition(event.currentTarget);
  };

  useEffect(() => {
    if (!listRef.current) {
      return;
    }

    const currentListElement = listRef.current;
    const animationFrameId = requestAnimationFrame(() => {
      updateScrollPosition(currentListElement);
    });
    const timeoutId = window.setTimeout(() => {
      if (listRef.current) {
        updateScrollPosition(listRef.current);
      }
    }, 120);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
    };
  }, [notifications, onScrolledToBottomChange, updateScrollPosition]);

  useEffect(() => {
    if (!listRef.current || !endOfListRef.current || !onScrolledToBottomChange) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        onScrolledToBottomChange(entry.isIntersecting);
      },
      {
        root: listRef.current,
        threshold: 1,
      }
    );

    observer.observe(endOfListRef.current);

    return () => {
      observer.disconnect();
    };
  }, [notifications.length, onScrolledToBottomChange]);

  return (
    <List
      ref={listRef}
      onScroll={handleListScroll}
      sx={{
        flex: hasNotifications ? 1 : '0 0 auto',
        height: hasNotifications ? '420px' : 'auto',
        minHeight: hasNotifications ? '420px' : 0,
        overflowY: hasNotifications ? 'auto' : 'hidden',
        p: 0,
        bgcolor: 'background.default',
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: 3,
          bgcolor: 'action.disabledBackground',
        },
      }}
    >
      {hasNotifications ? (
        notifications.map((notification, index) => (
          <ListItem
            key={notification.Id}
            disablePadding
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              ...(index === notifications.length - 1 ? { borderBottom: 'none' } : {}),
            }}
          >
            <NotificationItem
              notification={notification}
              setIsReadNotification={setIsReadNotification}
            />
          </ListItem>
        ))
      ) : listEmptyWhileFetching ? (
        <ListItem sx={{ py: 4, justifyContent: 'center', display: 'flex' }}>
          <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500 }}>
            Loading notifications…
          </Typography>
        </ListItem>
      ) : (
        <ListItem sx={{ py: 4, justifyContent: 'center', display: 'flex' }}>
          <Box sx={{ textAlign: 'center', px: 2 }}>
            <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500 }}>
              No notifications
            </Typography>
            <Typography color="text.disabled" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              You&apos;re all caught up
            </Typography>
          </Box>
        </ListItem>
      )}
      <ListItem ref={endOfListRef} sx={{ p: 0, height: 0, minHeight: 0, visibility: 'hidden' }} />
    </List>
  );
};

export default NotificationsList;
